import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { graphKeys, workBoardKeys } from '~/lib/query-keys';
import { useRestoreTrash, type GraphEntityDetail } from '~/hooks/queries/use-graph';
import { showUndoToast } from '~/lib/undo-toast';

/**
 * FRONT-892: the Work board's create/rename/archive/delete mutations over the
 * `project` graph entities (FRONT-2378 retired `routine`). Every hook
 * invalidates the whole graph cache (`graphKeys.all`) — matching
 * `useBulkDeleteEntities`/`useRestoreTrash`
 * in use-graph.ts — so the Active and Archived lists (which now cache under
 * distinct keys, see `graphKeys.entities`) both refresh together.
 *
 * The raw request functions (`createGraphEntity` etc.) are exported
 * separately from their hooks so the wire shape is unit-testable without a
 * QueryClient, mirroring `snoozeFeedItem` in use-feed-mutations.ts.
 */

/** Pull a human-readable message off an ApiError, same shape as cron/feed mutations. */
function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) {
    try {
      const parsed = JSON.parse(err.message);
      if (parsed?.error) return parsed.error;
    } catch {
      /* not JSON */
    }
    return err.message.slice(0, 200);
  }
  return fallback;
}

// FRONT-2378: `routine` retired as a Work container kind — the one kind a
// caller can create is `project`. Kept as a (single-member) type alias
// rather than a bare literal so `CreateEntityInput` didn't need touching.
export type WorkEntityKind = 'project';

export interface CreateEntityInput {
  type: WorkEntityKind;
  name: string;
}

export function createGraphEntity(input: CreateEntityInput) {
  return api.post<GraphEntityDetail>('/api/graph/entities', input);
}

/** POST /api/graph/entities — creates a project, lands in the Active list. */
export function useCreateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createGraphEntity,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: graphKeys.all });
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
      toast.success('Project created');
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to create')),
  });
}

export interface RenameEntityInput {
  id: string;
  name: string;
}

export function renameGraphEntity({ id, name }: RenameEntityInput) {
  return api.put<GraphEntityDetail>(`/api/graph/entity/${encodeURIComponent(id)}`, { name });
}

/**
 * PUT /api/graph/entity/:id — no hook-level toast: the Work board's cards
 * own their own toast via `useCardRename` (see `use-card-rename.ts`), and
 * the Memory page's entity-detail panel owns its own — both call the shared
 * `fireEntitySaveToast` (`~/lib/entity-save-outcome.ts`), but pass it
 * different default messages and (Work board only) the card's pre-save
 * archived state (FRONT-1109/FRONT-1154), so a generic hook-level toast here
 * would have to lose that per-surface nuance. This mutation deliberately
 * stays toast-free.
 */
export function useRenameEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: renameGraphEntity,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: graphKeys.all });
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
    },
  });
}

export interface ArchiveEntityInput {
  id: string;
  archived: boolean;
}

export function archiveGraphEntity({ id, archived }: ArchiveEntityInput) {
  return api.post<{ archived: boolean }>(
    `/api/graph/entity/${encodeURIComponent(id)}/archive`,
    { archived },
  );
}

/**
 * POST /api/graph/entity/:id/archive — also the Reactivate action
 * (`archived: false`). FRONT-892 refinement: the toast copy makes clear that
 * setting an item inactive keeps it in memory (nothing about the wire
 * contract or the `archived` field name changes, only what the user reads).
 */
export function useArchiveEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: archiveGraphEntity,
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: graphKeys.all });
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
      toast.success(
        variables.archived ? "Set to inactive — kept in EDDY's memory" : 'Reactivated',
      );
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to update')),
  });
}

/** FRONT-1216: per-item outcome of a bulk set-aside / reactivate. */
export interface BulkArchiveItemResult {
  id: string;
  status: 'changed' | 'not_found' | 'conflict' | 'error';
  error?: string;
}

export interface BulkArchiveResult {
  summary: { total: number; changed: number; failed: number };
  results: BulkArchiveItemResult[];
}

export interface BulkArchiveInput {
  ids: string[];
  archived: boolean;
}

export function bulkArchiveGraphEntities({ ids, archived }: BulkArchiveInput) {
  return api.post<BulkArchiveResult>('/api/graph/entities/bulk-archive', { ids, archived });
}

/**
 * POST /api/graph/entities/bulk-archive — set aside or reactivate a whole
 * selection at once.
 *
 * No toast here, unlike `useArchiveEntity`: a batch can PARTLY succeed
 * (reactivating an entry whose name an active entry already holds is refused,
 * FRONT-1097), and "Reactivated" over a result where two of twenty were
 * refused would be a lie. The caller owns the message because only it knows
 * how to word the partial case.
 */
export function useBulkArchiveEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: bulkArchiveGraphEntities,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: graphKeys.all });
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
    },
  });
}

export interface MergeEntitiesInput {
  winnerId: string;
  loserId: string;
}

export interface MergeEntitiesResult {
  ok: boolean;
  message: string;
  aliasAdded: boolean;
}

export function mergeGraphEntities({ winnerId, loserId }: MergeEntitiesInput) {
  return api.post<MergeEntitiesResult>('/api/graph/entities/merge', { winnerId, loserId });
}

/**
 * POST /api/graph/entities/merge — FRONT-1265's "combine these two entries"
 * control behind the Memory list's Merge action. Same invalidation shape as
 * `useDeleteEntity`/`useArchiveEntity` (the whole graph cache + the Work
 * board, since a merge can involve project containers) — a merge can
 * move attributes/relationships across entities and retire the loser, which
 * touches the same read paths a delete or archive does.
 *
 * No optimistic settling: the toast only fires from the real response, using
 * the backend's own `message` verbatim so the wording always matches what
 * actually happened server-side.
 */
export function useMergeEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: mergeGraphEntities,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: graphKeys.all });
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
      toast.success(data.message);
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to combine entries')),
  });
}

export interface DeleteEntityResult {
  deleted: boolean;
  batchId?: string;
}

export function deleteGraphEntity(id: string) {
  return api.delete<DeleteEntityResult>(`/api/graph/entity/${encodeURIComponent(id)}`);
}

/**
 * DELETE /api/graph/entity/:id — soft-delete into the recycle bin (the same
 * endpoint the Memory page's single-row delete uses). Success toast carries
 * an Undo action powered by the returned `batchId`, mirroring
 * `useEntitySelection.runBulkDelete`.
 */
export function useDeleteEntity() {
  const qc = useQueryClient();
  const restore = useRestoreTrash();
  return useMutation({
    mutationFn: deleteGraphEntity,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: graphKeys.all });
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
      showUndoToast({
        onUndo: data.batchId ? () => restore.mutateAsync({ batchId: data.batchId }) : undefined,
      });
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to delete')),
  });
}
